/**
 * Single formatter for employee phone numbers.
 *
 * Numbers have been stored in several shapes over time - bare national numbers
 * ("7984637399"), numbers with stray whitespace or tabs ("9924344998\t\t"),
 * already-internationalised numbers ("+919020839202"), international numbers
 * from other countries ("+1 (344) 373-9655"), and numbers whose country code was
 * written twice ("+91919181231323"). Every one of those has to render sensibly,
 * so the raw value is reduced to digits first and repaired before formatting.
 */

import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";

/** Numbers with no country code are assumed to be Indian. */
const DEFAULT_DIAL_CODE = "91";
const NATIONAL_NUMBER_LENGTH = 10;

/**
 * Reduce any stored value to a digit string, repairing a duplicated country code.
 *
 * @param {string|number} raw
 * @returns {string} digits only, or "" when there is nothing usable
 */
export const normalizePhoneDigits = (raw) => {
  if (raw === null || raw === undefined) return "";
  let digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";

  // "91" written in front of a number that already carried it. Guarded on length
  // so a genuine Indian number starting 91 (e.g. 919198765789) is left alone:
  // a correct +91 number is 12 digits, anything longer that starts 9191 is doubled.
  while (
    digits.length > DEFAULT_DIAL_CODE.length + NATIONAL_NUMBER_LENGTH &&
    digits.startsWith(DEFAULT_DIAL_CODE + DEFAULT_DIAL_CODE)
  ) {
    digits = digits.slice(DEFAULT_DIAL_CODE.length);
  }

  // A bare national number - prefix the default country code
  if (digits.length === NATIONAL_NUMBER_LENGTH) {
    digits = DEFAULT_DIAL_CODE + digits;
  }

  return digits;
};

/**
 * Format a stored phone number for display, e.g. "+91 98765 67898".
 * Falls back to "+<digits>" when the number cannot be parsed, and to `emptyValue`
 * when there is no number at all.
 *
 * @param {string|number} raw
 * @param {string} [emptyValue="-"]
 * @returns {string}
 */
export const formatPhoneNumber = (raw, emptyValue = "-") => {
  const digits = normalizePhoneDigits(raw);
  if (!digits) return emptyValue;

  const withPlus = `+${digits}`;
  try {
    if (isValidPhoneNumber(withPlus)) {
      return parsePhoneNumber(withPlus).formatInternational();
    }
  } catch (err) {
    // Unparseable - fall through to the plain representation below
  }
  return withPlus;
};

export default formatPhoneNumber;
